import { Box, IconButton, Tooltip, Typography, Paper } from '@mui/material';
import {
  Add as AddIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
} from '@mui/icons-material';
import type { NotebookPage } from '@/types/notebook';

type PageNavigationProps = {
  pages: NotebookPage[];
  currentPageIndex: number;
  onPageSelect: (index: number) => void;
  onAddPage: () => void;
  canEdit: boolean;
};

export const PageNavigation = ({
  pages,
  currentPageIndex,
  onPageSelect,
  onAddPage,
  canEdit,
}: PageNavigationProps) => {
  const hasPrev = currentPageIndex > 0;
  const hasNext = currentPageIndex < pages.length - 1;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: 120,
        height: '100%',
        backgroundColor: 'background.default',
        borderRight: 1,
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Pages
        </Typography>
        {canEdit && (
          <Tooltip title="Add Page">
            <IconButton size="small" onClick={onAddPage}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Page Thumbnails */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {pages.map((page, index) => (
          <Paper
            key={page.id}
            onClick={() => onPageSelect(index)}
            elevation={index === currentPageIndex ? 3 : 0}
            sx={{
              width: '100%',
              aspectRatio: '794 / 1123', // A4 aspect ratio
              cursor: 'pointer',
              border: 2,
              borderColor: index === currentPageIndex ? 'primary.main' : 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              backgroundColor: '#fff',
              position: 'relative',
              transition: 'border-color 0.2s, box-shadow 0.2s',
              '&:hover': {
                borderColor: 'primary.light',
              },
            }}
          >
            {page.thumbnail ? (
              <Box
                component="img"
                src={page.thumbnail}
                alt={`Page ${index + 1}`}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {index + 1}
                </Typography>
              </Box>
            )}
            {/* Page number badge */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 2,
                right: 2,
                backgroundColor: 'rgba(0,0,0,0.6)',
                color: '#fff',
                borderRadius: 0.5,
                px: 0.5,
                fontSize: '0.65rem',
              }}
            >
              {index + 1}
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          p: 1,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Tooltip title="Previous Page">
          <span>
            <IconButton
              size="small"
              onClick={() => onPageSelect(currentPageIndex - 1)}
              disabled={!hasPrev}
            >
              <PrevIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="caption">
          {currentPageIndex + 1} / {pages.length}
        </Typography>
        <Tooltip title="Next Page">
          <span>
            <IconButton
              size="small"
              onClick={() => onPageSelect(currentPageIndex + 1)}
              disabled={!hasNext}
            >
              <NextIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};
